<script setup>
import { ref, onMounted, computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLeadSubmissionsStore } from 'dashboard/stores/leadSubmissions';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import SubmissionDrawer from './SubmissionDrawer.vue';

const { t } = useI18n();
const store = useLeadSubmissionsStore();

const drawerRef = ref(null);
const selectedSubmission = ref(null);
const isDrawerOpen = ref(false);

const filters = reactive({
  status: '',
  lead_source_id: '',
  q: '',
});

const STATUS_OPTIONS = [
  { value: '', label: t('LEADS.INBOX.FILTERS.STATUS_ALL') },
  { value: 'pending_triage', label: t('LEADS.INBOX.FILTERS.STATUS_PENDING') },
  { value: 'error', label: t('LEADS.INBOX.FILTERS.STATUS_ERROR') },
  { value: 'processing', label: t('LEADS.INBOX.FILTERS.STATUS_PROCESSING') },
  { value: 'received', label: t('LEADS.INBOX.FILTERS.STATUS_RECEIVED') },
];

const COLUMN_META = {
  received_at:    { label: t('LEADS.INBOX.COLUMNS.RECEIVED_AT'), sortable: true },
  source_name:    { label: t('LEADS.INBOX.COLUMNS.SOURCE'),       sortable: false },
  contact_name:   { label: t('LEADS.INBOX.COLUMNS.CONTACT'),     sortable: true },
  intent_name:    { label: t('LEADS.INBOX.COLUMNS.INTENT'),      sortable: false },
  classification: { label: t('LEADS.INBOX.COLUMNS.CLASSIFICATION'), sortable: false },
  ai_summary:     { label: t('LEADS.INBOX.COLUMNS.SUMMARY'),     sortable: false },
};

const sortState = reactive({ field: 'received_at', order: '-' });

function buildSort() {
  return `${sortState.order}${sortState.field}`;
}

async function fetchSubmissions(page = 1) {
  await store.fetch({ ...filters, page, sort: buildSort() });
}

async function applyFilters() {
  await fetchSubmissions(1);
}

function clearFilters() {
  Object.assign(filters, { status: '', lead_source_id: '', q: '' });
  fetchSubmissions(1);
}

function openSubmission(submission) {
  selectedSubmission.value = submission;
  isDrawerOpen.value = true;
  drawerRef.value?.open();
}

function onDrawerClose() {
  isDrawerOpen.value = false;
  selectedSubmission.value = null;
}

function formatDate(iso) {
  if (!iso) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  }).format(new Date(iso));
}

function classificationColor(cls) {
  const map = { hot: 'text-n-ruby-11', warm: 'text-n-amber-11', cold: 'text-n-slate-11' };
  return map[cls?.toLowerCase()] || 'text-n-slate-11';
}

function rowClass(status) {
  if (status === 'error') return '!bg-n-ruby-4/10';
  if (status === 'pending_triage') return '';
  return '';
}

function isActing(id) {
  return store.actingOn === id;
}

// SelectInput não expõe @change - reaplicar filtro ao mudar o status
// (substitui o `@change="applyFilters"` do <select> nativo removido).
watch(() => filters.status, () => applyFilters());

onMounted(() => fetchSubmissions(1));
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Toolbar: filtros -->
    <div class="flex flex-wrap items-center gap-3">
      <div class="relative w-72 group">
        <Input
          v-model="filters.q"
          type="search"
          :placeholder="t('LEADS.INBOX.FILTERS.SEARCH')"
          :custom-input-class="[
            'h-9 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-9 rtl:!pr-9',
          ]"
          class="w-full"
          @keyup.enter="applyFilters"
        >
          <template #prefix>
            <Icon
              icon="i-lucide-search"
              class="absolute top-1/2 -translate-y-1/2 text-n-slate-11 group-focus-within:text-n-brand size-4 ltr:left-3 rtl:right-3"
            />
          </template>
        </Input>
      </div>

      <div class="w-44">
        <SelectInput v-model="filters.status" :options="STATUS_OPTIONS" />
      </div>

      <button
        v-if="filters.status || filters.lead_source_id || filters.q"
        type="button"
        class="px-3 py-1.5 text-body-small text-n-blue-11 hover:underline"
        @click="clearFilters"
      >
        {{ t('LEADS.INBOX.FILTERS.CLEAR') }}
      </button>

      <span class="ml-auto text-body-small text-n-slate-11">
        {{ store.meta.total_count }} {{ store.meta.total_count === 1 ? 'submissão' : 'submissões' }}
      </span>
    </div>

    <!-- Loading -->
    <div v-if="store.loading && !store.list.length" class="flex items-center justify-center py-16">
      <Spinner />
    </div>

    <!-- Empty -->
    <div v-else-if="!store.list.length && !store.loading" class="flex flex-col items-center justify-center py-24 text-n-slate-11 gap-3">
      <span class="size-14 rounded-2xl bg-n-alpha-2 flex items-center justify-center">
        <i class="i-lucide-inbox size-7" />
      </span>
      <div>
        <h2 class="text-heading-2 text-n-slate-12 mb-1.5">{{ t('LEADS.INBOX.EMPTY.TITLE') }}</h2>
        <p class="text-body-main text-n-slate-11 max-w-md mx-auto">{{ t('LEADS.INBOX.EMPTY.SUBTITLE') }}</p>
      </div>
    </div>

    <!-- Tabela -->
    <div v-else class="overflow-x-auto rounded-lg border border-n-weak">
      <table class="w-full text-body-small">
        <thead>
          <tr class="border-b border-n-weak bg-n-alpha-1">
            <th
              v-for="(meta, key) in COLUMN_META"
              :key="key"
              class="px-3 py-2.5 text-n-slate-11 font-medium text-left first:w-40"
            >{{ meta.label }}</th>
            <th class="px-3 py-2.5 w-24" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="s in store.list"
            :key="s.id"
            class="border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1 transition-colors"
            :class="rowClass(s.status)"
          >
            <td class="px-3 py-2.5 text-n-slate-12 whitespace-nowrap">{{ formatDate(s.received_at) }}</td>
            <td class="px-3 py-2.5 text-n-slate-12">
              <span class="inline-flex items-center gap-1.5">
                <i class="i-lucide-rss size-3.5 text-n-slate-10" />
                {{ s.source_name || '-' }}
              </span>
            </td>
            <td class="px-3 py-2.5 text-n-slate-12 font-medium">{{ s.contact_name || t('LEADS.INBOX.ROW.NO_NAME') }}</td>
            <td class="px-3 py-2.5">
              <div class="flex flex-col">
                <span class="text-n-slate-12">{{ s.intent_name || t('LEADS.INBOX.ROW.NO_INTENT') }}</span>
                <span v-if="s.classification_confidence" class="text-label-small text-n-slate-11">
                  {{ t('LEADS.INBOX.ROW.CONFIDENCE', { value: Math.round(s.classification_confidence * 100) }) }}
                </span>
              </div>
            </td>
            <td class="px-3 py-2.5">
              <span
                v-if="s.classification"
                class="inline-flex px-1.5 py-0.5 rounded text-label-small font-medium uppercase"
                :class="classificationColor(s.classification)"
              >
                {{ s.classification }}
              </span>
              <span v-else class="text-n-slate-11">-</span>
            </td>
            <td class="px-3 py-2.5 text-n-slate-11 text-xs max-w-xs truncate">{{ s.ai_summary || t('LEADS.INBOX.ROW.NO_SUMMARY') }}</td>
            <td class="px-3 py-2.5">
              <div class="flex items-center gap-1.5 justify-end">
                <button
                  v-tooltip="t('LEADS.INBOX.ACTIONS.OPEN')"
                  type="button"
                  class="size-9 rounded-lg border border-n-weak flex items-center justify-center text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 hover:border-n-slate-7 transition-colors disabled:opacity-40"
                  :disabled="isActing(s.id)"
                  @click="openSubmission(s)"
                >
                  <i class="i-lucide-external-link size-[1.05rem]" />
                </button>
                <button
                  v-tooltip="t('LEADS.INBOX.ACTIONS.REPROCESS')"
                  type="button"
                  class="size-9 rounded-lg border border-n-weak flex items-center justify-center text-n-blue-11 hover:bg-n-blue-3 hover:border-n-blue-8 transition-colors disabled:opacity-40"
                  :disabled="isActing(s.id)"
                  @click="store.reprocess(s.id)"
                >
                  <i class="i-lucide-refresh-cw size-[1.05rem]" :class="{ 'animate-spin': isActing(s.id) }" />
                </button>
                <button
                  v-tooltip="t('LEADS.INBOX.ACTIONS.CONVERT')"
                  type="button"
                  class="size-9 rounded-lg border border-n-weak flex items-center justify-center text-n-brand hover:bg-n-brand/10 hover:border-n-brand transition-colors disabled:opacity-40"
                  :disabled="isActing(s.id)"
                  @click="openSubmission(s)"
                >
                  <i class="i-lucide-check-circle size-[1.05rem]" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Paginação -->
    <div v-if="store.meta.total_pages > 1" class="flex items-center justify-between">
      <span class="text-body-small text-n-slate-11">
        {{ t('LEADS.INBOX.PAGINATION.PAGE_OF', { current: store.meta.current_page, total: store.meta.total_pages }) }}
      </span>
      <div class="flex gap-2">
        <button
          :disabled="store.meta.current_page <= 1"
          class="px-3 py-1 text-body-small rounded border border-n-weak hover:bg-n-alpha-1 disabled:opacity-40 disabled:cursor-not-allowed"
          @click="fetchSubmissions(store.meta.current_page - 1)"
        >{{ t('LEADS.INBOX.PAGINATION.PREV') }}</button>
        <button
          :disabled="store.meta.current_page >= store.meta.total_pages"
          class="px-3 py-1 text-body-small rounded border border-n-weak hover:bg-n-alpha-1 disabled:opacity-40 disabled:cursor-not-allowed"
          @click="fetchSubmissions(store.meta.current_page + 1)"
        >{{ t('LEADS.INBOX.PAGINATION.NEXT') }}</button>
      </div>
    </div>

    <!-- Drawer de detail -->
    <SubmissionDrawer
      ref="drawerRef"
      :submission="selectedSubmission"
      @close="onDrawerClose"
      @converted="fetchSubmissions(store.meta.current_page)"
      @discarded="fetchSubmissions(store.meta.current_page)"
      @reprocessed="fetchSubmissions(store.meta.current_page)"
    />
  </div>
</template>